import {
  createContext, useCallback, useContext, useRef, useState, type ReactNode,
} from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";

/**
 * The one "are you sure?" dialog for everything that isn't a task completion.
 *
 * It exists because the app had two answers to the same question. Task complete
 * and task delete went through CompleteConfirmDialog / DeleteConfirmDialog — real
 * dialogs, themed, naming what is about to happen. Every OTHER destructive action
 * (25 of them, including deleting a whole PROJECT from the same row as the task
 * that confirms properly) called the native window.confirm: an OS popup that
 * ignores the 5-theme system, cannot bold the subject's name, cannot carry a
 * consequence line, and reads as a browser malfunction next to the styled one.
 *
 * The shape here is deliberately CompleteConfirmDialog's shape — same title case,
 * same bolded-name description, same trailing `lines`, same Cancel — so the two
 * are indistinguishable to a user who meets both.
 *
 * Promise-based on purpose. The call sites it replaces were all
 * `if (confirm("...")) doThing()`, and a hook that returns a promise keeps that
 * exact control flow (`if (await confirm({...})) doThing()`) instead of forcing
 * open/target state into fifteen unrelated components.
 */
export interface ConfirmRequest {
  /** Sentence case, ends in a question mark. "Delete this invoice?" */
  title: string;
  /** Bolded subject — the thing being acted on. */
  name?: string | null;
  /** What is acted on, for the no-name fallback: "This invoice will be deleted." */
  subject?: string;
  /** Sentence completing the name. Must read after both the name and the subject. */
  body?: string;
  /** Extra consequences, one sentence each. */
  lines?: string[];
  confirmLabel?: string;
  /** Only override where "Cancel" would misread — see PortalProject's "Not Yet". */
  cancelLabel?: string;
  /** Red confirm button. Anything that destroys or cannot be undone. */
  destructive?: boolean;
}

type Confirm = (req: ConfirmRequest) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [req, setReq] = useState<ConfirmRequest | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const settle = useCallback((ok: boolean) => {
    const resolve = resolver.current;
    resolver.current = null;
    setReq(null);
    resolve?.(ok);
  }, []);

  const confirm = useCallback<Confirm>((next) => {
    return new Promise<boolean>((resolve) => {
      // A second ask supersedes one still on screen; the abandoned caller must be
      // told "no" rather than left waiting on a promise that can never settle.
      resolver.current?.(false);
      resolver.current = resolve;
      setReq(next);
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={req !== null}
        onOpenChange={(open) => {
          // Esc, the overlay, and Cancel all land here. Action settles true first
          // and clears the resolver, so this close can only ever be a no-op then.
          if (!open) settle(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{req?.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {req?.name
                ? <><span className="font-medium text-foreground">{req.name}</span> {req.body ?? "will be deleted."}</>
                : req?.subject
                  ? <>This {req.subject} {req.body ?? "will be deleted."}</>
                  : <>{req?.body}</>}
              {(req?.lines ?? []).map((line) => <span key={line}> {line}</span>)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{req?.cancelLabel ?? "Cancel"}</AlertDialogCancel>
            <AlertDialogAction
              className={req?.destructive ? buttonVariants({ variant: "destructive" }) : undefined}
              onClick={() => settle(true)}
            >
              {req?.confirmLabel ?? "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Confirm {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside ConfirmProvider");
  return ctx;
}
